import { useEffect, useMemo } from 'react';
import { Box, Divider, Grid } from '@mui/material';
import { FormikHelpers } from 'formik';
import useSWR from 'swr';
import { FormPart } from '@modules/shared/components/FormPart';
import { IFormFieldConfig } from '@shared/components/FormFieldsConstructor';
import { CheckboxField } from '@shared/fields/CheckboxField';
import { CheckboxListField } from '@shared/fields/CheckboxListField';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { RadioButtonListField } from '@shared/fields/RadioButtonListField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { TextInputField } from '@shared/fields/TextInputField';
import { GeneralFormFooter } from '@styles/Admin-tools/LenderUtilityStyles';
import { colorTheme } from '@styles/muiTheme';
import { SERVICES } from '@shared/constants/apiConfig';
import { ILienHolder } from '@modules/ContactManager/components/Tabs/components/Assets/interfaces';
import { bureausList } from '../../shared/data';
import { LenderUtilityFormPart } from '../../LenderUtilityFormPart';
import { LenderUtilityGridItem } from '../../LenderUtilityGridItem';
import { TLenderUtilityLoanInfoForm } from './validation';

interface ILoanInfoFormProps {
  create?: boolean;
  values?: TLenderUtilityLoanInfoForm;
  setFieldValue: FormikHelpers<TLenderUtilityLoanInfoForm>['setFieldValue'];
}

export type TLenderFormFieldConfig = IFormFieldConfig<TLenderUtilityLoanInfoForm>;

const valuationMethods = [
  { value: 'KbbRetail', label: 'KBB Retail' },
  { value: 'KbbWholesale', label: 'KBB Wholesale' },
  { value: 'KbbFinanceExpress', label: 'KBB Finance Express' },
  { value: 'Msrp', label: 'MSRP' },
  { value: 'NadaRetail', label: 'JD Power Retail' },
  { value: 'NadaTradeIn', label: 'JD Power Trade-in' },
];

const acceptedResidencyOptions = [
  { value: 'Citizen', label: 'Citizen' },
  { value: 'Resident', label: 'Permanent Resident' },
  { value: 'WorkVisa', label: 'Work Visa' },
  { value: 'Tin', label: 'ITIN' },
];

const acceptedResidencyPairOptions = [
  { value: 'Citizen_Citizen', label: 'Citizen/Citizen' },
  { value: 'Citizen_WorkVisa', label: 'Citizen/Work Visa' },
  { value: 'Citizen_Resident', label: 'Citizen/Permanent Resident' },
  { value: 'Citizen_Tin', label: 'Citizen/ITIN' },
  { value: 'WorkVisa_Resident', label: 'Work Visa/Permanent Resident' },
  { value: 'WorkVisa_WorkVisa', label: 'Work Visa/Work Visa' },
  { value: 'WorkVisa_Tin', label: 'Work Visa/ITIN' },
  { value: 'Resident_Resident', label: 'Permanent Resident/Permanent Resident' },
  { value: 'Resident_Tin', label: 'Permanent Resident/ITIN' },
  { value: 'Tin_Tin', label: 'ITIN/ITIN' },
];

const submissionMethodTypes = [
  { value: 'isDealertrack', label: 'Is Dealer Track' },
  { value: 'isOpenLending', label: 'Is Open Lending' },
  { value: 'isMeridian', label: 'Is Meridian' },
  { value: 'isQuilo', label: 'Is Quilo' },
  { value: 'isCudl', label: 'Is CUDL' },
  { value: 'isSubprime', label: 'Is Subprime' },
  { value: 'isOrigence', label: 'Is Origence' },

];

const ltvTypes = [
  { value: 'FrontEnd', label: 'Front End' },
  { value: 'AllIn', label: 'All In' },
];

export const LoanInfoForm = ({ create, values, setFieldValue }: ILoanInfoFormProps) => {
  const { data: lienHolders } = useSWR<ILienHolder[]>(`${SERVICES.ASSET}/v2/lienholder`);

  useEffect(() => {
    if (!values?.cuCollaborateCDFICheck && values?.isCUCollaborateOnly !== null) {
      setFieldValue('isCUCollaborateOnly', false);
    }
  }, [values?.cuCollaborateCDFICheck]);
  
  const lienHolderOptions = useMemo(() => {
    return lienHolders
      ?.filter((lh) => lh.name !== null)
      .map((lienHolder) => ({
        value: lienHolder.id,
        label: lienHolder.name,
      }));
  }, [lienHolders]);
  const lenderInformationForm: TLenderFormFieldConfig[] = [
    {
      disabled: !create,
      width: '130px',
      name: 'code',
    },
    {
      width: '130px',
      name: 'rank',
      label: 'Lender Rank',
    },
    {
      width: '130px',
      name: 'subRank',
      label: 'Lender Sub Rank',
    },
    {
      prefix: '$',
      decimalScale: 2,
      width: '130px',
      label: 'Funding Fee',
      name: 'generalInfo.fundingFeeGreen',
      component: NumberInputField,
    },
    {
      suffix: '%',
      decimalScale: 2,
      width: '130px',
      label: 'Max Flat Percent',
      name: 'generalInfo.maxFlatPercentGreen',
      component: NumberInputField,
    },
    {
      prefix: '$',
      decimalScale: 2,
      width: '130px',
      label: 'Max Flat Green',
      name: 'generalInfo.maxFlatGreen',
      component: NumberInputField,
    },
    {
      prefix: '$',
      decimalScale: 2,
      width: '130px',
      label: 'Max Cash Out',
      name: 'generalInfo.maxCashOutGreen',
      component: NumberInputField,
    },
    {
      width: '190px',
      name: 'generalInfo.availableMarkupIncrements',
      label: 'Available Markup Increments',
    },
    {
      prefix: '$',
      decimalScale: 2,
      width: '130px',
      name: 'generalInfo.achDiscount',
      label: 'ACH Discount',
      component: NumberInputField,
    },
    {
      width: '185px',
      name: 'nameOnDocs',
      showTooltip: true,
      component: TextInputField,
      maxLen: 31,
    },
    {
      width: '185px',
      label: 'Contact Name',
      name: 'name',
    },
    {
      width: '160px',
      name: 'contactPhone',
      component: PhoneInputField,
    },
    {
      width: '155px',
      name: 'generalInfo.daysToFirstPaymentGreen',
      label: 'Days to First Pmt',
      component: NumberInputField,
    },
    {
      width: '155px',
      name: 'generalInfo.approvalDaysGreen',
      label: 'Approval Good for Days',
      component: NumberInputField,
    },
    {
      width: '155px',
      name: 'generalInfo.maxMileageGreen',
      label: 'Max Mileage',
      component: NumberInputField,
    },
    {
      width: '155px',
      name: 'generalInfo.maxAgeGreen',
      label: 'Max Age',
      component: NumberInputField,
    },
    {
      label: 'Is APR',
      name: 'generalInfo.useAprBasedCalculation',
      component: CheckboxField,
    },
    {
      label: 'Allow Employer Zip',
      name: 'generalInfo.allowEmployerZip',
      component: CheckboxField,
    },
    {
      label: 'FL Stamp Fee',
      name: 'isFlStampFee',
      component: CheckboxField,
    },
    {
      label: 'Accepts MLA',
      name: 'acceptsMla',
      component: CheckboxField,
    },
    {
      label: 'CuCollab',
      name: 'cuCollaborateCDFICheck',
      component: CheckboxField,
      onChange: (value: boolean) => {
        setFieldValue('cuCollaborateCDFICheck', value);
        if (!value && value !== null && values?.isCUCollaborateOnly && values?.isCUCollaborateOnly !== null) {
          setFieldValue('isCUCollaborateOnly', false);
        }
      },
    },
    {
      label: 'CuCollab-Only',
      name: 'isCUCollaborateOnly',
      component: CheckboxField,
      disabled: !values?.cuCollaborateCDFICheck,
    },
     {
      label: 'ACH Required',
      name: 'isACHRequired',
      component: CheckboxField,
    },
  ];

  const submissionMethod: TLenderFormFieldConfig[] = [
    {
      name: 'submissionMethod',
      label: 'Submission Method',
      options: submissionMethodTypes,
      component: RadioButtonListField,
      onChange: (value: string) => {
        setFieldValue('submissionMethodType', value);

        setFieldValue('isDealertrack', value === 'isDealertrack');
        setFieldValue('isOpenLending', value === 'isOpenLending');
        setFieldValue('isMeridian', value === 'isMeridian');
        setFieldValue('isQuilo', value === 'isQuilo');
        setFieldValue('isCudl', value === 'isCudl');
        setFieldValue('isSubprime', value === 'isSubprime');
        setFieldValue('isOrigence', value === 'isOrigence');
        setFieldValue('submissionMethod', value);
      },
    },
  ];

  const valuationMethodForm: TLenderFormFieldConfig[] = [
    {
      name: 'generalInfo.defaultValuationMethods',
      label: '',
      options: valuationMethods,
      component: CheckboxListField,
    },
  ];

  const acceptedResidency: TLenderFormFieldConfig[] = [
    {
      name: 'generalInfo.acceptedResidency',
      label: '',
      options: acceptedResidencyOptions,
      component: CheckboxListField,
    },
  ];

  const acceptedResidencyPairs: TLenderFormFieldConfig[] = [
    {
      width: '100%',
      name: 'generalInfo.acceptedResidencyPairs',
      component: SelectAutocompleteField,
      margin: '10px 0 0 0',
      options: acceptedResidencyPairOptions,
      multiple: true,
    },
  ];

  const prohibitedLienHolders: TLenderFormFieldConfig[] = [
    {
      width: '100%',
      name: 'generalInfo.prohibitedLienHolders',
      component: SelectAutocompleteField,
      margin: '10px 0 0 0',
      options: lienHolderOptions,
      multiple: true,
    },
  ];

  const borrowerRulesForm: TLenderFormFieldConfig[] = [
    {
      width: '104px',
      suffix: '%',
      decimalScale: 2,
      name: 'generalInfo.maxDtiGreen',
      label: 'Max DTI',
      component: NumberInputField,
    },
    {
      width: '104px',
      suffix: '%',
      decimalScale: 2,
      name: 'generalInfo.maxPtiGreen',
      label: 'Max PTI',
      component: NumberInputField,
    },
    {
      width: '148px',
      name: 'generalInfo.minPrimaryFicoGreen',
      label: 'Min Primary Score',
    },
    {
      width: '232px',
      name: 'generalInfo.defaultScore',
      label: 'Default Score',
      component: TextInputField,
      disabled: true,
    },
    {
      width: '232px',
      name: 'generalInfo.preferredScore',
      label: 'Preferred Score',
      options: bureausList,
      component: SelectAutocompleteField,
    },
  ];

  const ltvTypeForm: TLenderFormFieldConfig[] = [
    {
      name: 'ltvType',
      label: '',
      options: ltvTypes,
      component: RadioButtonListField,
    },
    {
      width: '232px',
      name: 'useBothLtv',
      label: 'Use Both LTV',
      component: CheckboxField,
    },
  ];

  const dealerTrackIdForm: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      label: 'Dealer Track ID',
      name: 'dealertrackId',
      component: TextInputField,
    },
  ];

  const meridianLendersForm: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      label: 'Meridian API UserID',
      name: 'meridianConfiguration.userId',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Meridian Url',
      name: 'meridianConfiguration.meridianDecisionLoanUrl',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Meridian API Password',
      name: 'meridianConfiguration.password',
      component: TextInputField,
      isPassword: true,
    },
    {
      width: '231px',
      label: 'Meridian Submit Doc Url',
      name: 'meridianConfiguration.meridianSubmitDocUrl',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Lender Reference ID',
      name: 'meridianLenderReferenceId',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Org Reference ID',
      name: 'meridianOrgReferenceId',
      component: TextInputField,
    },
  ];

  const cudlLendersForm: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      label: 'Code',
      name: 'cudlConfiguration.code',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'User ID',
      name: 'cudlConfiguration.userId',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Password',
      name: 'cudlConfiguration.password',
      component: TextInputField,
      isPassword: true,
    },
    {
      width: '231px',
      label: 'Organization Code',
      name: 'cudlConfiguration.organizationCode',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'URL',
      name: 'cudlConfiguration.url',
      component: TextInputField,
    },
    {
      width: '231px',
      label: 'Decision Auth Key',
      name: 'cudlConfiguration.decisionAuthKey',
      component: TextInputField,
    },
  ];

  const openLendersForm: TLenderFormFieldConfig[] = [
    {
      width: '231px',
      name: 'openLendingConfiguration.userName',
      label: 'User Name',
    },
    {
      width: '231px',
      name: 'openLendingConfiguration.password',
      label: 'Password',
      type: 'password',
      component: TextInputField,
    },
    {
      width: '231px',
      name: 'openLendingConfiguration.institutionId',
      label: 'Institution Id',
    },
    {
      width: '231px',
      name: 'openLendingConfiguration.endpointUrl',
      label: 'Endpoint URL',
    },
  ];

  const yellowForm: TLenderFormFieldConfig[] = [
    {
      name: 'generalInfo.daysToFirstPaymentYellow',
      label: 'Days to First Pmt',
    },
    {
      name: 'generalInfo.maxDtiYellow',
      label: 'Max DTI',
      suffix: '%',
    },
    {
      name: 'generalInfo.maxPtiYellow',
      label: 'Max PTI',
      suffix: '%',
    },
    {
      name: 'generalInfo.maxMileageYellow',
      label: 'Max Mileage',
    },
    {
      name: 'generalInfo.maxAgeYellow',
      label: 'Max Age',
    },
    {
      name: 'generalInfo.minPrimaryFicoYellow',
      label: 'Min Primary Score',
    },
    // {
    //   width: '164px',
    //   name: 'generalInfo.minSecondaryFicoYellow',
    //   label: 'Min Secondary Score',
    // },
    {
      name: 'generalInfo.approvalDaysYellow',
      label: 'Approval Days',
    },
    {
      name: 'generalInfo.maxCashOutYellow',
      label: 'Max Cash Out',
      prefix: '$',
    },
    {
      name: 'generalInfo.fundingFeeYellow',
      label: 'Funding Fee',
      prefix: '$',
    },
  ];

  const formFieldsConfig = {
    lender: lenderInformationForm,
    submissionMethod: submissionMethod,
    valuationMethod: valuationMethodForm,
    prohibitedLienHolders: prohibitedLienHolders,
    acceptedResidency: acceptedResidency,
    acceptedResidencyPairs: acceptedResidencyPairs,
    borrowerRules: borrowerRulesForm.map(
      (item) =>
        ({
          component: NumberInputField,
          ...item,
        }) as TLenderFormFieldConfig
    ),
    dealerTrack: dealerTrackIdForm,
    ltvType: ltvTypeForm,
    openLenders: openLendersForm,
    meridian: meridianLendersForm,
    origence: cudlLendersForm,
    yellow: yellowForm.map((item) => ({
      width: '148px',
      component: NumberInputField,
      ...item,
    })),
  };

  return useMemo(
    () => (
      <Grid
        container
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        columnSpacing={2}
      >
        <Grid
          container
          spacing={2}
          sx={{ flexGrow: 1, padding: 2 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={8}
            spacing={2}
          >
            <Grid
              container
              direction="column"
              rowGap={2}
            >
              <LenderUtilityGridItem
                direction="column"
                align="flex-start"
              >
                <LenderUtilityFormPart
                  name="Auto Loan Info and Rules"
                  config={formFieldsConfig.lender}
                />
              </LenderUtilityGridItem>

              <LenderUtilityGridItem
                direction="row"
                align="center"
              >
                <LenderUtilityFormPart
                  name="Accepted Residency"
                  config={formFieldsConfig.acceptedResidency}
                />
                <Divider
                  flexItem
                  orientation="vertical"
                  variant="middle"
                  sx={{ borderColor: colorTheme.palette.common.black, marginX: 2 }}
                />
                <LenderUtilityFormPart
                  name="Accepted Residency Pairs (Two Borrowers)"
                  config={formFieldsConfig.acceptedResidencyPairs}
                />
              </LenderUtilityGridItem>
              <LenderUtilityGridItem justify="flex-start">
                <LenderUtilityFormPart
                  name="Borrower Rules"
                  config={formFieldsConfig.borrowerRules}
                />
              </LenderUtilityGridItem>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            spacing={2}
          >
            <Grid
              container
              direction="column"
              rowGap={2}
            >
              <LenderUtilityGridItem
                direction="column"
                align="flex-start"
              >
                <LenderUtilityFormPart
                  name="Submission Method"
                  config={submissionMethod}
                />
                {values?.isDealertrack && <LenderUtilityFormPart config={dealerTrackIdForm} />}
                {values?.isOpenLending && <LenderUtilityFormPart config={openLendersForm} />}
                {values?.isMeridian && <LenderUtilityFormPart config={meridianLendersForm} />}
                {values?.isOrigence && <LenderUtilityFormPart config={cudlLendersForm} />}
              </LenderUtilityGridItem>
              <LenderUtilityGridItem
                direction="column"
                align="flex-start"
              >
                <LenderUtilityFormPart
                  name="Default Valuation Method"
                  config={formFieldsConfig.valuationMethod}
                />
                <LenderUtilityFormPart
                  name="LTV Type"
                  config={formFieldsConfig.ltvType}
                />
              </LenderUtilityGridItem>
              <LenderUtilityGridItem
                direction="column"
                align="flex-start"
              >
                <LenderUtilityFormPart
                  name="Prohibited Lien Holders"
                  config={formFieldsConfig.prohibitedLienHolders}
                />
              </LenderUtilityGridItem>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ padding: 2 }}>
          <GeneralFormFooter>
            <FormPart
              name="Yellow"
              config={formFieldsConfig.yellow}
            />
          </GeneralFormFooter>
        </Box>
      </Grid>
    ),
    [formFieldsConfig]
  );
};
